import api from '../api';
import dayjs from 'dayjs';

/**
 * Client-Side Notification Aggregator for Staff Web
 * ZERO DATABASE WRITES - Fully dynamic in-memory aggregation
 * Tracks read states in localStorage
 */

const getStorageKey = (userId) => `staff_read_notifications_${userId || 'default'}`;

export const getReadNotificationIds = (userId) => {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (_) {
    return [];
  }
};

export const markNotificationAsRead = (notifId, userId) => {
  try {
    const current = getReadNotificationIds(userId);
    if (!current.includes(notifId)) {
      const updated = [...current, notifId];
      localStorage.setItem(getStorageKey(userId), JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('staff-notif-updated'));
    }
  } catch (_) {}
};

export const markAllNotificationsAsRead = (notifIds = [], userId) => {
  try {
    const current = getReadNotificationIds(userId);
    const set = new Set([...current, ...notifIds]);
    localStorage.setItem(getStorageKey(userId), JSON.stringify(Array.from(set)));
    window.dispatchEvent(new CustomEvent('staff-notif-updated'));
  } catch (_) {}
};

export const fetchAllStaffNotifications = async (userId) => {
  const notifications = [];
  const readIds = new Set(getReadNotificationIds(userId));

  // Current month key for roster / salary
  const currentMonthKey = dayjs().format('YYYY-MM');

  // 1. LEAVE UPDATES (Approved / Rejected)
  try {
    const leaveRes = await api.get('/leave/me').catch(() => null);
    const leaves = leaveRes?.data?.leaves || leaveRes?.data || [];
    if (Array.isArray(leaves)) {
      leaves.forEach((lv) => {
        const status = (lv.status || '').toUpperCase();
        if (status === 'APPROVED' || status === 'REJECTED') {
          const dateStr = lv.reviewedAt || lv.updatedAt || lv.createdAt;
          const notifId = `leave_${lv.id}_${status}`;
          notifications.push({
            id: notifId,
            category: 'LEAVE',
            type: status === 'APPROVED' ? 'success' : 'error',
            title: status === 'APPROVED' ? 'Leave Approved' : 'Leave Rejected',
            message: `${lv.leaveType || 'Leave'} for ${lv.startDate ? dayjs(lv.startDate).format('DD MMM') : ''}${lv.endDate ? ' - ' + dayjs(lv.endDate).format('DD MMM YYYY') : ''} was ${status.toLowerCase()}.${lv.adminRemarks ? ' Note: ' + lv.adminRemarks : ''}`,
            timestamp: dateStr ? new Date(dateStr) : new Date(),
            actionUrl: '/staff/leave',
            actionText: 'View Leaves',
            isRead: readIds.has(notifId)
          });
        }
      });
    }
  } catch (_) {}

  // 2. EXPENSE UPDATES (Approved / Rejected)
  try {
    const expRes = await api.get('/me/expenses').catch(() => null);
    const expenses = expRes?.data?.claims || expRes?.data?.expenses || expRes?.data || [];
    if (Array.isArray(expenses)) {
      expenses.forEach((exp) => {
        const status = (exp.status || '').toUpperCase();
        if (status === 'APPROVED' || status === 'REJECTED') {
          const dateStr = exp.approvedAt || exp.rejectedAt || exp.updatedAt || exp.createdAt;
          const notifId = `expense_${exp.id}_${status}`;
          notifications.push({
            id: notifId,
            category: 'EXPENSES',
            type: status === 'APPROVED' ? 'success' : 'error',
            title: status === 'APPROVED' ? 'Expense Claim Approved' : 'Expense Claim Rejected',
            message: `Expense claim of ₹${exp.amount || 0} (${exp.title || exp.category || 'Claim'}) was ${status.toLowerCase()}.${exp.rejectionReason ? ' Reason: ' + exp.rejectionReason : ''}`,
            timestamp: dateStr ? new Date(dateStr) : new Date(),
            actionUrl: '/staff/expenses',
            actionText: 'View Expenses',
            isRead: readIds.has(notifId)
          });
        }
      });
    }
  } catch (_) {}

  // 3. SALARY PROCESSED & PAYSLIP AVAILABLE
  try {
    const salaryRes = await api.get('/me/salary-history-range').catch(() => null);
    if (salaryRes?.data?.success && salaryRes?.data?.earliestMonth) {
      // Latest processed month notice
      const monthKey = salaryRes.data.latestMonth || currentMonthKey;
      const notifIdSal = `salary_processed_${monthKey}`;
      notifications.push({
        id: notifIdSal,
        category: 'SALARY',
        type: 'info',
        title: 'Salary Processed',
        message: `Salary calculation for ${dayjs(monthKey, 'YYYY-MM').format('MMMM YYYY')} has been finalized.`,
        timestamp: new Date(),
        actionUrl: '/staff/salary',
        actionText: 'Check Salary',
        isRead: readIds.has(notifIdSal)
      });

      const notifIdSlip = `payslip_available_${monthKey}`;
      notifications.push({
        id: notifIdSlip,
        category: 'SALARY',
        type: 'info',
        title: 'Payslip Available',
        message: `Payslip for ${dayjs(monthKey, 'YYYY-MM').format('MMMM YYYY')} is ready for viewing and download.`,
        timestamp: new Date(),
        actionUrl: '/staff/salary',
        actionText: 'Download Payslip',
        isRead: readIds.has(notifIdSlip)
      });
    }
  } catch (_) {}

  // 4. NEW TASKS / ACTIVITIES
  try {
    const taskRes = await api.get('/activities/me').catch(() => null);
    const tasks = taskRes?.data?.activities || taskRes?.data?.tasks || taskRes?.data || [];
    if (Array.isArray(tasks)) {
      tasks.slice(0, 10).forEach((t) => {
        const notifId = `task_${t.id}_${t.status || 'NEW'}`;
        const isCompleted = (t.status || '').toUpperCase() === 'COMPLETED';
        notifications.push({
          id: notifId,
          category: 'TASKS',
          type: isCompleted ? 'success' : 'warning',
          title: isCompleted ? 'Task Completed' : 'Task Assigned / Update',
          message: `${t.title || 'Task'}${t.dueDate ? ' • Due: ' + dayjs(t.dueDate).format('DD MMM YYYY') : ''}${t.priority ? ' [' + t.priority + ']' : ''}`,
          timestamp: t.updatedAt || t.createdAt ? new Date(t.updatedAt || t.createdAt) : new Date(),
          actionUrl: '/staff/tasks',
          actionText: 'View Tasks',
          isRead: readIds.has(notifId)
        });
      });
    }
  } catch (_) {}

  // 5. SALES / ORDER UPDATES
  try {
    const salesRes = await api.get('/sales/my-orders').catch(() => null);
    const orders = salesRes?.data?.orders || salesRes?.data || [];
    if (Array.isArray(orders)) {
      orders.slice(0, 10).forEach((ord) => {
        const status = (ord.status || 'PENDING').toUpperCase();
        const notifId = `sales_order_${ord.id}_${status}`;
        let type = 'info';
        if (status === 'APPROVED' || status === 'DELIVERED') type = 'success';
        if (status === 'REJECTED' || status === 'CANCELLED') type = 'error';
        if (status === 'DISPATCHED') type = 'warning';

        notifications.push({
          id: notifId,
          category: 'SALES',
          type: type,
          title: `Order Update: #${ord.orderNumber || ord.id}`,
          message: `Order for ${ord.clientName || 'Client'} (₹${ord.totalAmount || 0}) status changed to ${status}.`,
          timestamp: ord.updatedAt || ord.createdAt ? new Date(ord.updatedAt || ord.createdAt) : new Date(),
          actionUrl: '/staff/sales',
          actionText: 'View Orders',
          isRead: readIds.has(notifId)
        });
      });
    }
  } catch (_) {}

  // 6. ROSTER / SHIFT CHANGES
  try {
    const rosterRes = await api.get('/roster/requests/my').catch(() => null);
    const requests = rosterRes?.data?.requests || rosterRes?.data || [];
    if (Array.isArray(requests)) {
      requests.forEach((req) => {
        const status = (req.status || 'PENDING').toUpperCase();
        const notifId = `roster_req_${req.id}_${status}`;
        notifications.push({
          id: notifId,
          category: 'ROSTER',
          type: status === 'APPROVED' ? 'success' : status === 'REJECTED' ? 'error' : 'info',
          title: `Shift Request ${status}`,
          message: `Shift change request for ${req.date ? dayjs(req.date).format('DD MMM YYYY') : 'scheduled date'} was ${status.toLowerCase()}.`,
          timestamp: req.updatedAt || req.createdAt ? new Date(req.updatedAt || req.createdAt) : new Date(),
          actionUrl: '/staff/roster',
          actionText: 'View Roster',
          isRead: readIds.has(notifId)
        });
      });
    }

    // Also check current month roster changes
    const rosterMeRes = await api.get(`/roster/me?month=${currentMonthKey}`).catch(() => null);
    const shifts = rosterMeRes?.data?.roster || rosterMeRes?.data?.shifts || [];
    if (Array.isArray(shifts) && shifts.length > 0) {
      const latestShift = shifts[shifts.length - 1];
      if (latestShift?.updatedAt) {
        const notifIdShift = `roster_shift_${latestShift.id || currentMonthKey}`;
        notifications.push({
          id: notifIdShift,
          category: 'ROSTER',
          type: 'info',
          title: 'Roster Schedule Updated',
          message: `Your duty shift roster for ${dayjs(currentMonthKey, 'YYYY-MM').format('MMMM YYYY')} has been updated.`,
          timestamp: new Date(latestShift.updatedAt),
          actionUrl: '/staff/roster',
          actionText: 'Check Schedule',
          isRead: readIds.has(notifIdShift)
        });
      }
    }
  } catch (_) {}

  // Sort by timestamp descending (newest first)
  notifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return notifications;
};
